<div class="tile-padding-first tile-padding admin-page">

  @if (available === false) {
    <p id="liveActivityUnavailable">Live activity is not switched on in this environment.</p>
  }

  @if (available) {
    <div class="admin-toolbar admin-toolbar--center">
      <h2 class="admin-h2 tw:mr-auto">Live activity</h2>
      <div class="btn-group" role="group" aria-label="Time window">
        @for (w of windows; track w.minutes) {
          <button type="button" class="btn" [class.btn-primary]="minutes === w.minutes" [class.btn-secondary]="minutes !== w.minutes"
            [attr.aria-pressed]="minutes === w.minutes" [attr.data-window]="w.minutes" (click)="setWindow(w.minutes)">{{w.label}}</button>
        }
      </div>
      <button type="button" class="btn btn-secondary" id="liveActivityPause" [attr.aria-pressed]="paused" (click)="togglePause()">
        {{paused ? 'Resume auto-refresh' : 'Pause auto-refresh'}}</button>
      <button type="button" class="btn btn-secondary" id="liveActivityRefresh" [disabled]="loading" (click)="refreshNow()">Refresh now</button>
    </div>
    <p class="admin-lede">
      Who is using the system and how it is coping, over the last {{windowLabel}}.
      @if (refreshedAt) {
        Updated {{refreshedAt | date:'HH:mm:ss'}}{{paused ? ', auto-refresh paused' : ', refreshes every 30 seconds'}}.
      }
    </p>

    @if (error) {
      <div class="tw:my-2 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" id="liveActivityError" role="alert">{{error}}</div>
    }

    @if (summary) {
      <!-- KPI strip -->
      <div class="tw:grid tw:grid-cols-2 tw:md:grid-cols-5 tw:gap-3 tw:mb-6" id="liveActivityKpis">
        <div class="admin-panel tw:p-3 tw:border tw:rounded" data-kpi="users">
          <div class="tw:text-sm tw:text-gray-600">People using it</div>
          <div class="tw:text-2xl tw:font-bold">{{summary.totals.activeUsers | number}}</div>
          <div class="tw:text-sm tw:text-gray-600">+ {{summary.totals.serviceAccounts ?? 0 | number}} service account{{(summary.totals.serviceAccounts ?? 0) === 1 ? '' : 's'}}</div>
        </div>
        <div class="admin-panel tw:p-3 tw:border tw:rounded" data-kpi="requests">
          <div class="tw:text-sm tw:text-gray-600">Requests</div>
          <div class="tw:text-2xl tw:font-bold">{{summary.totals.requestCount | number}}</div>
          <div class="tw:text-sm tw:text-gray-600">calls to the server</div>
        </div>
        <div class="admin-panel tw:p-3 tw:border tw:rounded" data-kpi="failures"
          [class.tw:border-red-500]="isHighFailure(summary.totals.failurePercent)" [class.tw:bg-red-50]="isHighFailure(summary.totals.failurePercent)"
          title="A failure is a request the server could not complete. Over 5% is shown in red.">
          <div class="tw:text-sm tw:text-gray-600">Failed</div>
          <div class="tw:text-2xl tw:font-bold" [class.tw:text-red-700]="isHighFailure(summary.totals.failurePercent)">
            {{summary.totals.failurePercent | number:'1.0-1'}}%</div>
          <div class="tw:text-sm tw:text-gray-600">{{summary.totals.failureCount | number}} of {{summary.totals.requestCount | number}} requests</div>
        </div>
        <div class="admin-panel tw:p-3 tw:border tw:rounded" data-kpi="p95"
          [class.tw:border-red-500]="isSlow(summary.totals.p95Ms)" [class.tw:bg-red-50]="isSlow(summary.totals.p95Ms)"
          title="95% of requests finished within this time (the 95th percentile). Over 2 s is shown in red.">
          <div class="tw:text-sm tw:text-gray-600">Response time</div>
          <div class="tw:text-2xl tw:font-bold" [class.tw:text-red-700]="isSlow(summary.totals.p95Ms)">{{duration(summary.totals.p95Ms)}}</div>
          <div class="tw:text-sm tw:text-gray-600">95% within this; typical {{duration(summary.totals.p50Ms)}}</div>
        </div>
        <div class="admin-panel tw:p-3 tw:border tw:rounded" data-kpi="switched-off">
          <div class="tw:text-sm tw:text-gray-600">Stopped by a switch</div>
          <div class="tw:text-2xl tw:font-bold tw:text-gray-700">{{summary.totals.switchedOffCount ?? 0 | number}}</div>
          <div class="tw:text-sm tw:text-gray-600">did nothing: switched off on System Controls</div>
        </div>
      </div>

      <ng-template #userTable let-users>
        <div class="tw:overflow-x-auto">
          <table class="admin-table tw:w-full">
            <thead>
              <tr><th>Name</th><th>Type</th><th>Role</th><th>Last seen</th><th>Last action</th><th class="tw:text-right">Actions</th></tr>
            </thead>
            <tbody>
              @for (u of users; track u.userId) {
                <tr class="tw:cursor-pointer tw:hover:bg-gray-50" [class.tw:bg-blue-50]="selectedUser?.userId === u.userId"
                  [attr.data-user]="u.userId" tabindex="0" [attr.aria-expanded]="selectedUser?.userId === u.userId"
                  title="Show what they did" (click)="selectUser(u)" (keydown.enter)="selectUser(u)">
                  <td>{{userName(u)}}@if (u.email && (u.firstName || u.lastName)) {<div class="tw:text-xs tw:text-gray-600"><admin-email [value]="u.email" /></div>}</td>
                  <td data-user-type>{{userTypeLabel(u)}}</td>
                  <td data-user-roles>{{roleNames(u)}}</td>
                  <td [title]="utc(u.lastSeenUtc) | date:'medium'">{{ago(u.lastSeenUtc)}}</td>
                  <td data-user-last-action [title]="request(null, u.lastPath)">{{op(u.lastActionType)}}
                    @if (isFailedStatus(u.lastStatusCode)) {<div class="tw:text-xs tw:text-red-700" data-user-last-status>{{status(u.lastStatusCode)}}</div>}
                    @if (u.lastSwitchedOff) {<div class="tw:text-xs tw:text-gray-600" data-switched-off>Stopped: {{switchNames(u.lastSwitchedOff)}} is off</div>}</td>
                  <td class="tw:text-right">{{u.actionCount | number}}</td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </ng-template>

      <!-- Active now -->
      <section class="admin-section" id="liveActivityUsers">
        <h3 class="admin-h3">Active now</h3>
        <p class="admin-lede">People who used the system in the last {{windowLabel}}, most recent first. Internal is SEG staff; External is tutors, clients and learners. Click a person to see what they did.</p>
        @if (summary.activeUsers.length === 0) {
          <p class="tw:text-sm tw:text-gray-600" data-empty="users">Nobody has used the system in the last {{windowLabel}}. Out of hours that is normal; in the working day, check the system can be reached.</p>
        } @else {
          <ng-container *ngTemplateOutlet="userTable; context: { $implicit: summary.activeUsers }"></ng-container>
        }
      </section>

      @if (summary.serviceAccounts?.length) {
        <section class="admin-section" id="liveActivityServiceAccounts">
          <h3 class="admin-h3">Service accounts</h3>
          <p class="admin-lede">Automated callers, such as the tutor nudger, that sign in as users. Listed apart from people; which sign-ins count is set by <i>Live activity: service accounts</i> on System Controls &gt; Settings.</p>
          <ng-container *ngTemplateOutlet="userTable; context: { $implicit: summary.serviceAccounts }"></ng-container>
        </section>
      }
      @if (selectedUser) {
        <section class="admin-section" id="liveActivityRecent">
          <div class="admin-toolbar">
            <h3 class="admin-h3 tw:mr-auto">What {{userName(selectedUser)}} did</h3>
            <button type="button" class="btn btn-link" (click)="selectUser(selectedUser)">Close</button>
          </div>
          <p class="admin-lede">Their latest 50 actions in the last 24 hours, newest first. Hover over an action for the address it called. IP addresses are masked; nothing they typed is shown.</p>
          @if (recentError) {
            <div class="tw:my-2 tw:p-3 tw:border tw:border-red-500 tw:bg-red-50 tw:text-red-900 tw:rounded" role="alert">{{recentError}}</div>
          }
          @if (recentLoading && recent.length === 0) {
            <p class="tw:text-sm tw:text-gray-600">Loading…</p>
          } @else if (recent.length === 0 && !recentError) {
            <p class="tw:text-sm tw:text-gray-600" data-empty="recent">They have done nothing in the last 24 hours.</p>
          } @else {
            <div class="tw:overflow-x-auto">
              <table class="admin-table tw:w-full">
                <thead>
                  <tr><th>When</th><th>Action</th><th>Result</th><th class="tw:text-right">Took</th><th>Network</th></tr>
                </thead>
                <tbody>
                  @for (a of recent; track $index) {
                    <tr data-recent>
                      <td [title]="utc(a.createdDateUtc) | date:'medium'">{{ago(a.createdDateUtc)}}</td>
                      <td [title]="request(a.httpMethod, a.path)">{{op(a.actionType)}}</td>
                      <td data-recent-status [title]="a.statusCode != null ? 'HTTP ' + a.statusCode : ''"
                        [class.tw:text-red-700]="(!a.success && a.reason !== 'renewed') || (a.statusCode ?? 0) >= 500">{{status(a.statusCode, a.success)}}@if (a.switchedOff) {<div class="tw:text-xs tw:text-gray-600" data-recent-switched-off>Stopped: {{switchNames(a.switchedOff)}} is off</div>}@if (a.reason) {<div class="tw:text-xs tw:text-gray-600" data-recent-reason>{{reasonLabel(a.reason)}}</div>}</td>
                      <td class="tw:text-right" [class.tw:text-red-700]="isSlow(a.durationMs)">{{duration(a.durationMs)}}</td>
                      <td title="The first half of the IP address only">{{a.maskedIpAddress || '-'}}</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </section>
      }

      <!-- Slowest and most-failing operations, side by side -->
      <div class="tw:grid tw:grid-cols-1 tw:lg:grid-cols-2 tw:gap-6">
        <section class="admin-section" id="liveActivitySlowest">
          <h3 class="admin-h3">Slowest</h3>
          <p class="admin-lede">What took longest. Over 2 s is shown in red: people will notice the wait.</p>
          @if (summary.slowestOperations.length === 0) {
            <p class="tw:text-sm tw:text-gray-600" data-empty="slowest">Nothing was timed in this window, so there is nothing to compare.</p>
          } @else {
            <table class="admin-table tw:w-full">
              <thead>
                <tr><th>Operation</th><th class="tw:text-right">Requests</th>
                  <th class="tw:text-right" title="95% of requests finished within this time">Usually within</th><th class="tw:text-right">Slowest</th></tr>
              </thead>
              <tbody>
                @for (o of summary.slowestOperations; track o.actionType) {
                  <tr data-slow>
                    <td [title]="o.actionType">{{op(o.actionType)}}</td>
                    <td class="tw:text-right">{{o.requestCount | number}}</td>
                    <td class="tw:text-right" [class.tw:text-red-700]="isSlow(o.p95Ms)" [class.tw:font-bold]="isSlow(o.p95Ms)">{{duration(o.p95Ms)}}</td>
                    <td class="tw:text-right">{{duration(o.maxMs)}}</td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </section>

        <section class="admin-section" id="liveActivityFailing">
          <h3 class="admin-h3">Failing</h3>
          <p class="admin-lede">What failed most. Over 5% of an operation's requests is shown in red.</p>
          @if (summary.failingOperations.length === 0) {
            <p class="tw:text-sm tw:text-gray-600" data-empty="failing">Nothing failed in this window.</p>
          } @else {
            <table class="admin-table tw:w-full">
              <thead>
                <tr><th>Operation</th><th class="tw:text-right">Failed</th><th class="tw:text-right">Requests</th><th class="tw:text-right">Failure rate</th></tr>
              </thead>
              <tbody>
                @for (o of summary.failingOperations; track o.actionType) {
                  <tr data-failing>
                    <td [title]="o.actionType">{{op(o.actionType)}}</td>
                    <td class="tw:text-right">{{o.failureCount | number}}</td>
                    <td class="tw:text-right">{{o.requestCount | number}}</td>
                    <td class="tw:text-right" [class.tw:text-red-700]="isHighFailure(operationFailurePercent(o))"
                      [class.tw:font-bold]="isHighFailure(operationFailurePercent(o))">{{operationFailurePercent(o) | number:'1.0-1'}}%</td>
                  </tr>
                }
              </tbody>
            </table>
          }
        </section>
      </div>

      <!-- Stopped by a switch -->
      <section class="admin-section" id="liveActivitySwitchedOff">
        <h3 class="admin-h3">Stopped by a switch</h3>
        <p class="admin-lede">Calls that returned without doing their work because a switch on System Controls is off. Expected while something is switched off; not a failure.</p>
        @if (!summary.switchedOffOperations?.length) {
          <p class="tw:text-sm tw:text-gray-600" data-empty="switched-off">Nothing was stopped by a switch in this window.</p>
        } @else {
          <table class="admin-table tw:w-full">
            <thead>
              <tr><th>Operation</th><th class="tw:text-right">Stopped</th><th class="tw:text-right">Requests</th><th>Switched off</th></tr>
            </thead>
            <tbody>
              @for (o of summary.switchedOffOperations; track o.actionType) {
                <tr data-switched-off-op>
                  <td [title]="o.actionType">{{op(o.actionType)}}</td>
                  <td class="tw:text-right">{{o.switchedOffCount | number}}</td>
                  <td class="tw:text-right">{{o.requestCount | number}}</td>
                  <td [title]="switches(o.switchedOffKeys)">{{switchNames(o.switchedOffKeys)}}</td>
                </tr>
              }
            </tbody>
          </table>
        }
      </section>

      <!-- Sign-in rejections -->
      <section class="admin-section" id="liveActivityAuthRejections">
        <h3 class="admin-h3">Refused sign-ins</h3>
        <p class="admin-lede">Requests turned away because the sign-in was missing, had run out or was not ours, newest first. A person is named only when the sign-in was genuinely ours. Grey rows were renewed automatically, which is routine.</p>
        @if (!summary.authRejections?.length) {
          <p class="tw:text-sm tw:text-gray-600" data-empty="rejections">No sign-ins were refused in this window.</p>
        } @else {
          <div class="tw:overflow-x-auto">
            <table class="admin-table tw:w-full">
              <thead>
                <tr><th>When</th><th>Who</th><th>Type</th><th>Why</th><th>Trying to</th></tr>
              </thead>
              <tbody>
                @for (r of summary.authRejections; track $index) {
                  <tr data-auth-rejection [class.tw:text-gray-500]="r.reason === 'renewed'">
                    <td [title]="utc(r.createdDateUtc) | date:'medium'">{{ago(r.createdDateUtc)}}</td>
                    <td [title]="r.maskedIpAddress ? 'From network ' + r.maskedIpAddress : 'Network not known'">{{rejectedBy(r)}}</td>
                    <td data-rejection-type>{{rejectedType(r)}}</td>
                    <td>{{reasonLabel(r.reason)}}</td>
                    <td data-rejection-request [title]="request(r.httpMethod, r.path)">{{requestName(r.httpMethod, r.path)}}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      </section>

      <!-- Background jobs -->
      <section class="admin-section" id="liveActivityJobs">
        <h3 class="admin-h3">Background jobs</h3>
        <p class="admin-lede">Work the system does on its own, such as copying clients and bookings from Orbit. The latest run of each job in this window; a failed run is shown in red.</p>
        @if (summary.backgroundJobs.length === 0) {
          <p class="tw:text-sm tw:text-gray-600" data-empty="jobs">No background job ran in this window. The Orbit syncs run every few minutes while they are switched on, so if this stays empty, check System Controls.</p>
        } @else {
          <table class="admin-table tw:w-full">
            <thead>
              <tr><th>Job</th><th>Last run</th><th>Result</th><th class="tw:text-right">Took</th><th class="tw:text-right">Runs</th><th class="tw:text-right">Failed runs</th></tr>
            </thead>
            <tbody>
              @for (j of summary.backgroundJobs; track j.jobName) {
                <tr [attr.data-job]="j.jobName">
                  <td [title]="jobHelp(j.jobName)">{{job(j.jobName)}}</td>
                  <td [title]="utc(j.lastRunUtc) | date:'medium'">{{ago(j.lastRunUtc)}}</td>
                  <td [class.tw:text-red-700]="!j.lastSuccess" [class.tw:font-bold]="!j.lastSuccess">{{j.lastSuccess ? 'Succeeded' : 'Failed'}}</td>
                  <td class="tw:text-right">{{duration(j.lastDurationMs)}}</td>
                  <td class="tw:text-right">{{j.runs | number}}</td>
                  <td class="tw:text-right" [class.tw:text-red-700]="j.failures > 0">{{j.failures | number}}</td>
                </tr>
              }
            </tbody>
          </table>
        }
      </section>
    }
  }
</div>
